import { WarningOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { InputRef } from 'antd/lib/input';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import {
  fetchAllLeadTodos,
  fetchTodoAssignees,
  createStandaloneTodo,
  updateLeadTodoStatus,
  updateStandaloneTodo,
  type LeadTodoListFilters,
  type StandaloneTodoPayload
} from './leadTodosApi';
import {
  formatDateTimeFull,
  formatDateTimeShort,
  statusLabelMap,
  statusTagColors,
  todoPriorityColors,
  todoPriorityLabels,
  todoStatusLabels
} from './constants';
import type { LeadTodo, LeadTodoPriority, LeadTodoStatus, TodoAssignee } from './types';
import { useGlobalShortcuts } from './hooks/useGlobalShortcuts';

const { Text } = Typography;

const statusOptions = Object.entries(todoStatusLabels).map(([value, label]) => ({
  value,
  label
}));

const priorityOptions = Object.entries(todoPriorityLabels).map(([value, label]) => ({
  value,
  label
}));

type LeadOption = {
  id: string;
  company_name?: string | null;
  status?: string | null;
};

type LeadTodosPageProps = {
  availableLeads?: LeadOption[];
  onOpenLead?: (leadId: string) => void;
};

type CreateTodoFormValues = {
  title: string;
  description?: string;
  lead_id?: string | null;
  status: LeadTodoStatus;
  priority: LeadTodoPriority;
  due_at: Dayjs | null;
  assigned_to: string | null;
  sync_to_calendar: boolean;
};

export default function LeadTodosPage({ availableLeads = [], onOpenLead }: LeadTodosPageProps) {
  const [todos, setTodos] = useState<LeadTodo[]>([]);
  const [assignees, setAssignees] = useState<TodoAssignee[]>([]);
  const [loading, setLoading] = useState(false);
  const [assigneesLoading, setAssigneesLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<LeadTodoStatus[]>([]);
  const [assigneeFilter, setAssigneeFilter] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creatingTodo, setCreatingTodo] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState<LeadTodo | null>(null);
  const [updatingTodo, setUpdatingTodo] = useState(false);
  const [createForm] = Form.useForm<CreateTodoFormValues>();
  const [editForm] = Form.useForm<CreateTodoFormValues>();
  const watchedCreateDue = Form.useWatch('due_at', createForm);
  const watchedEditDue = Form.useWatch('due_at', editForm);
  const searchInputRef = useRef<InputRef | null>(null);

  const formValuesToPayload = useCallback(
    (values: CreateTodoFormValues): StandaloneTodoPayload | null => {
      const title = typeof values.title === 'string' ? values.title.trim() : '';
      if (!title) {
        message.error('Titel skal udfyldes');
        return null;
      }

      const descriptionValue = typeof values.description === 'string' ? values.description.trim() : '';
      const dueValue = values.due_at ? values.due_at.toDate().toISOString() : null;
      const syncValue = Boolean(values.sync_to_calendar);

      if (syncValue && !dueValue) {
        message.error('Tilføj en forfaldsdato for at synkronisere til kalenderen');
        return null;
      }

      return {
        title,
        description: descriptionValue ? descriptionValue : null,
        status: values.status,
        priority: values.priority,
        due_at: dueValue,
        assigned_to: values.assigned_to || null,
        sync_to_calendar: syncValue,
        lead_id: values.lead_id || null
      };
    },
    []
  );

  const loadTodos = useCallback(
    async (overrides?: Partial<LeadTodoListFilters>) => {
      try {
        setLoading(true);
        const data = await fetchAllLeadTodos({
          status: statusFilter,
          assigned_to: assigneeFilter,
          search: searchTerm,
          includeCompleted,
          overdueOnly,
          ...overrides
        });
        setTodos(data);
      } catch (error: any) {
        console.error('Failed to load todos', error);
        message.error(error?.message || 'Kunne ikke hente opgaver');
      } finally {
        setLoading(false);
      }
    },
    [assigneeFilter, includeCompleted, overdueOnly, searchTerm, statusFilter]
  );

  const loadAssignees = useCallback(async () => {
    try {
      setAssigneesLoading(true);
      const data = await fetchTodoAssignees();
      setAssignees(data);
    } catch (error: any) {
      console.error('Failed to load assignees', error);
      message.error(error?.message || 'Kunne ikke hente brugere');
    } finally {
      setAssigneesLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTodos();
  }, [loadTodos]);

  useEffect(() => {
    void loadAssignees();
  }, [loadAssignees]);

  const isClosed = useCallback((todo: LeadTodo) => {
    return todo.status === 'DONE' || todo.status === 'CANCELLED';
  }, []);

  const isOverdue = useCallback(
    (todo: LeadTodo) => {
      if (!todo.due_at || isClosed(todo)) {
        return false;
      }
      const dueDate = new Date(todo.due_at);
      return !Number.isNaN(dueDate.getTime()) && dueDate.getTime() < Date.now();
    },
    [isClosed]
  );

  const sortedTodos = useMemo(() => {
    return [...todos].sort((a, b) => {
      const overdueDiff = Number(isOverdue(a)) - Number(isOverdue(b));
      if (overdueDiff !== 0) {
        return overdueDiff * -1;
      }
      const closedDiff = Number(isClosed(a)) - Number(isClosed(b));
      if (closedDiff !== 0) {
        return closedDiff;
      }
      const dateA = a.due_at ? new Date(a.due_at).getTime() : Number.POSITIVE_INFINITY;
      const dateB = b.due_at ? new Date(b.due_at).getTime() : Number.POSITIVE_INFINITY;
      if (dateA === dateB) {
        return new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
      }
      return dateA - dateB;
    });
  }, [isClosed, isOverdue, todos]);

  const handleSearch = useCallback((value: string) => {
    const normalized = value.trim();
    setSearchInput(value);
    setSearchTerm(normalized);
  }, []);

  const handleStatusChange = useCallback(
    async (todo: LeadTodo, nextStatus: LeadTodoStatus) => {
      if (nextStatus === todo.status) {
        return;
      }
      setStatusUpdatingId(todo.id);
      try {
        await updateLeadTodoStatus(todo.lead_id, todo.id, nextStatus);
        message.success('Status opdateret');
        await loadTodos();
      } catch (error: any) {
        console.error('Failed to update todo status', error);
        message.error(error?.message || 'Kunne ikke opdatere status');
      } finally {
        setStatusUpdatingId((current) => (current === todo.id ? null : current));
      }
    },
    [loadTodos]
  );

  const assigneeOptions = useMemo(
    () =>
      assignees.map((user) => ({
        value: user.id,
        label: user.full_name?.trim() || user.email || 'Ukendt bruger'
      })),
    [assignees]
  );

  const leadOptions = useMemo(
    () =>
      availableLeads.map((lead) => {
        const companyLabel = lead.company_name?.trim() || 'Ukendt virksomhed';
        const statusLabel = lead.status ? statusLabelMap.get(lead.status) || lead.status : null;
        const label = statusLabel ? `${companyLabel} · ${statusLabel}` : companyLabel;
        return {
          value: lead.id,
          label
        };
      }),
    [availableLeads]
  );

  const handleOpenCreateModal = useCallback(() => {
    createForm.setFieldsValue({
      title: '',
      description: '',
      lead_id: null,
      status: 'NEW' as LeadTodoStatus,
      priority: 'MEDIUM' as LeadTodoPriority,
      due_at: null,
      assigned_to: null,
      sync_to_calendar: false
    });
    setCreateModalOpen(true);
  }, [createForm]);

  const handleCloseCreateModal = useCallback(() => {
    setCreateModalOpen(false);
    createForm.resetFields();
  }, [createForm]);

  const handleCreateSubmit = useCallback(
    async (values: CreateTodoFormValues) => {
      const payload = formValuesToPayload(values);
      if (!payload) {
        return;
      }

      try {
        setCreatingTodo(true);
        await createStandaloneTodo(payload);
        message.success('Opgave oprettet');
        handleCloseCreateModal();
        await loadTodos();
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke oprette opgave');
      } finally {
        setCreatingTodo(false);
      }
    },
    [formValuesToPayload, handleCloseCreateModal, loadTodos]
  );

  const handleOpenEditModal = useCallback(
    (todo: LeadTodo) => {
      setEditingTodo(todo);
      editForm.setFieldsValue({
        title: todo.title,
        description: todo.description || '',
        lead_id: todo.lead_id || null,
        status: todo.status as LeadTodoStatus,
        priority: todo.priority as LeadTodoPriority,
        due_at: todo.due_at ? dayjs(todo.due_at) : null,
        assigned_to: todo.assigned_to || null,
        sync_to_calendar: todo.sync_to_calendar
      });
      setEditModalOpen(true);
    },
    [editForm]
  );

  const handleCloseEditModal = useCallback(() => {
    setEditModalOpen(false);
    setEditingTodo(null);
    editForm.resetFields();
  }, [editForm]);

  const handleEditSubmit = useCallback(
    async (values: CreateTodoFormValues) => {
      if (!editingTodo) {
        message.error('Ingen opgave valgt');
        return;
      }
      const payload = formValuesToPayload(values);
      if (!payload) {
        return;
      }

      try {
        setUpdatingTodo(true);
        await updateStandaloneTodo(editingTodo.id, payload);
        message.success('Opgave opdateret');
        handleCloseEditModal();
        await loadTodos();
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke opdatere opgave');
      } finally {
        setUpdatingTodo(false);
      }
    },
    [editingTodo, formValuesToPayload, handleCloseEditModal, loadTodos]
  );

  const handleFocusSearch = useCallback(() => {
    const input = searchInputRef.current;
    if (!input) {
      return;
    }
    input.focus({ cursor: 'all' });
    input.select?.();
  }, []);

  const handleRefreshTodos = useCallback(() => {
    void loadTodos();
  }, [loadTodos]);

  const handleToggleOverdue = useCallback(() => {
    setOverdueOnly((prev) => !prev);
  }, []);

  const shortcutsEnabled = !(createModalOpen || editModalOpen);

  const shortcutBindings = useMemo(
    () => [
      {
        key: 'k',
        ctrlOrMeta: true,
        handler: handleFocusSearch
      },
      {
        key: 't',
        ctrlOrMeta: true,
        shift: true,
        handler: handleOpenCreateModal
      },
      {
        key: 'r',
        ctrlOrMeta: true,
        handler: handleRefreshTodos
      },
      {
        key: 'o',
        ctrlOrMeta: true,
        shift: true,
        handler: handleToggleOverdue
      }
    ],
    [handleFocusSearch, handleOpenCreateModal, handleRefreshTodos, handleToggleOverdue]
  );

  useGlobalShortcuts(shortcutBindings, { enabled: shortcutsEnabled });

  const columns: ColumnsType<LeadTodo> = useMemo(
    () => [
      {
        title: 'Titel',
        key: 'title',
        render: (_text, record) => {
          const overdue = isOverdue(record);
          return (
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Space size={8} wrap>
                <span style={{ fontWeight: 600 }}>{record.title}</span>
                <Tag color={todoPriorityColors[record.priority] || 'default'}>
                  {todoPriorityLabels[record.priority] || record.priority}
                </Tag>
                {record.sync_to_calendar ? (
                  <Tag color="blue">Kalender</Tag>
                ) : null}
                {overdue ? (
                  <Tag color="red" icon={<WarningOutlined />}>Forfalden</Tag>
                ) : null}
              </Space>
              {record.description ? (
                <Text type="secondary">{record.description}</Text>
              ) : (
                <Text type="secondary">Ingen beskrivelse</Text>
              )}
            </Space>
          );
        }
      },
      {
        title: 'Status',
        dataIndex: 'status',
        render: (_value, record) => (
          <Select
            value={record.status}
            onChange={(value) => handleStatusChange(record, value as LeadTodoStatus)}
            options={statusOptions}
            style={{ minWidth: 160 }}
            loading={statusUpdatingId === record.id}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          />
        )
      },
      {
        title: 'Lead',
        key: 'lead',
        render: (_text, record) => {
          const hasLead = Boolean(record.lead_id);
          const leadStatus = record.lead_status ?? undefined;
          const leadStatusLabel = leadStatus ? statusLabelMap.get(leadStatus) : null;
          const leadStatusColor = leadStatus ? statusTagColors[leadStatus] : undefined;
          const companyLabel = record.lead_company_name?.trim() || (hasLead ? 'Ukendt virksomhed' : 'Ingen lead');
          return (
            <Space direction="vertical" size={2}>
              <span style={{ fontWeight: 500 }}>{companyLabel}</span>
              {hasLead ? (
                <Tag color={leadStatusColor || 'default'}>
                  {leadStatusLabel || leadStatus || 'Ukendt'}
                </Tag>
              ) : (
                <Tag color="default">Ingen lead</Tag>
              )}
            </Space>
          );
        }
      },
      {
        title: 'Forfald',
        dataIndex: 'due_at',
        render: (value: string | null, record) => {
          const overdue = isOverdue(record);
          const formatted = formatDateTimeShort(value) || 'Ingen dato';
          return (
            <Text type={overdue ? 'danger' : undefined}>
              {formatted}
              {value ? ` (${formatDateTimeFull(value)})` : ''}
            </Text>
          );
        }
      },
      {
        title: 'Ansvarlig',
        key: 'assignee',
        render: (_text, record) => {
          if (record.assigned_to_name && record.assigned_to_name.trim()) {
            return record.assigned_to_name.trim();
          }
          if (record.assigned_to_email) {
            return record.assigned_to_email;
          }
          return <Text type="secondary">Ikke angivet</Text>;
        }
      },
      {
        title: 'Opdateret',
        dataIndex: 'updated_at',
        render: (value: string) => formatDateTimeShort(value) || 'Ukendt'
      },
      {
        title: 'Handling',
        key: 'action',
        render: (_text, record) => (
          <Space size="small">
            <Button
              type="link"
              onClick={(event) => {
                event.stopPropagation();
                handleOpenEditModal(record);
              }}
            >
              Rediger
            </Button>
            <Button
              type="link"
              disabled={!record.lead_id}
              onClick={(event) => {
                event.stopPropagation();
                if (record.lead_id) {
                  onOpenLead?.(record.lead_id);
                }
              }}
            >
              Åbn lead
            </Button>
          </Space>
        )
      }
    ],
    [handleOpenEditModal, handleStatusChange, isOverdue, onOpenLead, statusUpdatingId]
  );

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card title="Lead-opgaver">
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8}>
            <Select
              mode="multiple"
              allowClear
              placeholder="Filtrer status"
              value={statusFilter}
              onChange={(values) => setStatusFilter(values as LeadTodoStatus[])}
              options={statusOptions}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              allowClear
              placeholder="Ansvarlig"
              value={assigneeFilter ?? undefined}
              onChange={(value) => setAssigneeFilter(value ?? null)}
              options={assigneeOptions}
              loading={assigneesLoading}
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={6}>
            <Input.Search
              placeholder="Søg titel eller virksomhed"
              allowClear
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              onSearch={handleSearch}
              ref={searchInputRef}
            />
          </Col>
          <Col xs={12} md={2}>
            <Space>
              <Text type="secondary">Afsluttede</Text>
              <Switch
                checked={includeCompleted}
                onChange={(checked) => setIncludeCompleted(checked)}
              />
            </Space>
          </Col>
          <Col xs={12} md={2}>
            <Space>
              <Text type="secondary">Kun forfaldne</Text>
              <Switch checked={overdueOnly} onChange={(checked) => setOverdueOnly(checked)} />
            </Space>
          </Col>
          <Col xs={24} md={6} style={{ textAlign: 'right' }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => void loadTodos()} loading={loading}>
                Opdater
              </Button>
              <Button type="primary" onClick={handleOpenCreateModal}>
                Ny opgave
              </Button>
            </Space>
          </Col>
          </Row>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Genveje: Cmd/Ctrl+K søg · Cmd/Ctrl+Shift+T ny opgave · Cmd/Ctrl+R opdater · Cmd/Ctrl+Shift+O kun forfaldne
          </Text>
        </Space>
      </Card>

      <Card>
        <Table
          dataSource={sortedTodos}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 900 }}
          locale={{ emptyText: <Empty description="Ingen opgaver" /> }}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          onRow={(record) => {
            const leadId = record.lead_id;
            const canOpen = Boolean(leadId && onOpenLead);
            return {
              onClick: canOpen && leadId ? () => onOpenLead?.(leadId) : undefined,
              style: { cursor: canOpen ? 'pointer' : 'default' }
            };
          }}
        />
      </Card>

      <Modal
        title="Ny opgave"
        open={createModalOpen}
        onCancel={handleCloseCreateModal}
        footer={null}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreateSubmit}>
          <Form.Item
            name="title"
            label="Titel"
            rules={[{ required: true, message: 'Angiv en titel' }]}
          >
            <Input placeholder="Kort beskrivelse" allowClear />
          </Form.Item>
          <Form.Item name="description" label="Beskrivelse">
            <Input.TextArea rows={3} allowClear placeholder="Detaljer" />
          </Form.Item>
          <Form.Item name="lead_id" label="Lead (valgfri)">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={leadOptions}
              placeholder="Vælg lead"
            />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Vælg status' }]}
              >
                <Select options={statusOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="priority"
                label="Prioritet"
                rules={[{ required: true, message: 'Vælg prioritet' }]}
              >
                <Select options={priorityOptions} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="due_at" label="Forfaldsdato">
                <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="assigned_to" label="Ansvarlig">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={assigneeOptions}
                  loading={assigneesLoading}
                  placeholder="Vælg bruger"
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="sync_to_calendar"
            label="Synkroniser til kalender"
            valuePropName="checked"
            extra="Kræver forfaldsdato"
          >
            <Switch disabled={!watchedCreateDue} />
          </Form.Item>
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleCloseCreateModal}>Annuller</Button>
            <Button type="primary" htmlType="submit" loading={creatingTodo}>
              Opret opgave
            </Button>
          </Space>
        </Form>
      </Modal>

      <Modal
        title="Rediger opgave"
        open={editModalOpen}
        onCancel={handleCloseEditModal}
        footer={null}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditSubmit}>
          <Form.Item
            name="title"
            label="Titel"
            rules={[{ required: true, message: 'Angiv en titel' }]}
          >
            <Input placeholder="Kort beskrivelse" allowClear />
          </Form.Item>
          <Form.Item name="description" label="Beskrivelse">
            <Input.TextArea rows={3} allowClear placeholder="Detaljer" />
          </Form.Item>
          <Form.Item name="lead_id" label="Lead (valgfri)">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={leadOptions}
              placeholder="Vælg lead"
            />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Vælg status' }]}
              >
                <Select options={statusOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="priority"
                label="Prioritet"
                rules={[{ required: true, message: 'Vælg prioritet' }]}
              >
                <Select options={priorityOptions} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="due_at" label="Forfaldsdato">
                <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="assigned_to" label="Ansvarlig">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={assigneeOptions}
                  loading={assigneesLoading}
                  placeholder="Vælg bruger"
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="sync_to_calendar"
            label="Synkroniser til kalender"
            valuePropName="checked"
            extra="Kræver forfaldsdato"
          >
            <Switch disabled={!watchedEditDue} />
          </Form.Item>
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleCloseEditModal}>Annuller</Button>
            <Button type="primary" htmlType="submit" loading={updatingTodo}>
              Gem ændringer
            </Button>
          </Space>
        </Form>
      </Modal>
    </Space>
  );
}
